import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
    fetchAllUsers,
    selectAllUsers,
    selectTotalUsers,
    selectUsersLoading,
    selectUsersError,
    clearError,
    resetUsersState,
    deleteUser,
} from '../_store/masterAuthSlice';
import UAVSphere_logo from "../assets/images/UAVspherelogo.png";

export const UsersList = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    
    const users = useSelector(selectAllUsers);
    const totalUsers = useSelector(selectTotalUsers);
    const loading = useSelector(selectUsersLoading);
    const error = useSelector(selectUsersError);
    
    // UI State
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState(''); // 'active', 'inactive', or ''
    const [sortBy, setSortBy] = useState('partner_no'); // 'partner_no', 'signup_time', 'name'
    const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
    const [selectedUser, setSelectedUser] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [showSuccessToast, setShowSuccessToast] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    
    const usersPerPage = 20;
    
    // Debounce search term
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            // Reset to page 1 when search term changes
            setCurrentPage(1);
        }, 500);
        
        return () => clearTimeout(timer);
    }, [searchTerm]);
    
    // Reset to page 1 when status filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [statusFilter, sortBy, sortOrder]);
    
    // Fetch users when filters change
    useEffect(() => {
        fetchUsers();
        
        return () => {
            dispatch(resetUsersState());
        };
    }, [dispatch, currentPage, debouncedSearchTerm, statusFilter, sortBy, sortOrder]);
    
    const fetchUsers = useCallback(async () => {
        try {
            // Map status filter to backend expected values
            let statusParam = '';
            if (statusFilter === 'active') statusParam = 'active';
            if (statusFilter === 'inactive') statusParam = 'inactive';
            
            const result = await dispatch(fetchAllUsers({ 
                page: currentPage,
                per_page: usersPerPage,
                search: debouncedSearchTerm,
                provider_type: '',
                verified: '',
                status: statusParam,
                sort_by: sortBy,
                sort_order: sortOrder
            })).unwrap();
            
            console.log('Fetched users:', result);
        } catch (error) {
            console.error('Failed to fetch users:', error);
            if (error.message === 'No session found') {
                navigate('/masterlogin');
            }
        }
    }, [dispatch, currentPage, debouncedSearchTerm, statusFilter, sortBy, sortOrder, navigate]);
    
    const handleRefresh = async () => {
        setIsRefreshing(true);
        await fetchUsers();
        setTimeout(() => setIsRefreshing(false), 500);
        showSuccessToastMessage('Users refreshed successfully');
    };
    
    const showSuccessToastMessage = (message) => {
        setSuccessMessage(message);
        setShowSuccessToast(true);
        setTimeout(() => setShowSuccessToast(false), 3000);
    };
    
    const handleBackToDashboard = () => {
        navigate('/superadmin');
    };
    
    const handleViewDetails = (user) => {
        setSelectedUser(user);
        setShowModal(true);
    };
    
    const handleSort = (column) => {
        if (sortBy === column) {
            // Toggle sort order
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            // New column, default to ascending
            setSortBy(column);
            setSortOrder('asc');
        }
        setCurrentPage(1);
    };
    
    const getSortIcon = (column) => {
        if (sortBy !== column) return ' ↕️';
        return sortOrder === 'asc' ? ' ↑' : ' ↓';
    };
    
    const getStatusBadge = (isActive) => {
        if (isActive) {
            return (
                <span style={{
                    ...styles.statusBadge,
                    background: '#28a745',
                    color: 'white'
                }}>
                    ✓ Active
                </span>
            );
        } else {
            return (
                <span style={{
                    ...styles.statusBadge,
                    background: '#dc3545',
                    color: 'white'
                }}>
                    ✗ Inactive
                </span>
            );
        }
    };
    
    const getVerifiedBadge = (isVerified) => {
        if (isVerified) {
            return (
                <span style={{
                    ...styles.verifiedBadge,
                    background: '#28a745',
                    color: 'white'
                }}>
                    ✓ Verified
                </span>
            );
        } else {
            return (
                <span style={{
                    ...styles.verifiedBadge,
                    background: '#ffc107',
                    color: '#000'
                }}>
                    ⏳ Pending
                </span>
            );
        }
    };
    
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric'
        });
    };

    // Step 1 — called from the 🗑️ Delete button
const handleDeleteClick = (user) => {
    setUserToDelete(user);
};

// Step 2 — called when admin confirms in the modal
const confirmDelete = async () => {
    if (!userToDelete) return;

    setIsDeleting(true);
    try {
        await dispatch(deleteUser(userToDelete._id)).unwrap();
        showSuccessToastMessage('Provider deleted successfully');
        setUserToDelete(null);
        fetchUsers();
    } catch (err) {
        console.error('Delete failed:', err);
    } finally {
        setIsDeleting(false);
    }
};

// Step 3 — cancel
const cancelDelete = () => {
    if (isDeleting) return; // don't allow cancel mid-request
    setUserToDelete(null);
};
    
    
    const styles = {
        container: {
            minHeight: '100vh',
            background: '#f5f5f5',
            padding: '1rem'
        },
        toast: {
            position: 'fixed',
            top: '20px',
            right: '20px',
            background: '#28a745',
            color: 'white',
            padding: '1rem 1.5rem',
            borderRadius: '8px',
            zIndex: 2000,
            animation: 'slideIn 0.3s ease-out',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        },
        header: {
            background: 'white',
            padding: '1.5rem 2rem',
            borderRadius: '12px',
            marginBottom: '1.5rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '1rem'
        },
        headerLeft: {
            display: 'flex',
            alignItems: 'center',
            gap: '1.5rem',
            flexWrap: 'wrap'
        },
        logo: {
            width: '120px',
            height: 'auto'
        },
        title: {
            fontSize: '1.75rem',
            fontWeight: '700',
            color: '#333',
            margin: 0
        },
        subtitle: {
            color: '#666',
            fontSize: '0.9rem',
            marginTop: '0.25rem'
        },
        headerRight: {
            display: 'flex',
            gap: '1rem',
            alignItems: 'center',
            flexWrap: 'wrap'
        },
        backButton: {
            padding: '0.6rem 1.5rem',
            background: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '0.9rem',
            fontWeight: '500',
            transition: 'all 0.2s'
        },
        statsCard: {
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '1rem 2rem',
            borderRadius: '12px',
            textAlign: 'center',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
        },
        statsNumber: {
            fontSize: '2rem',
            fontWeight: '700',
            color: 'white',
            marginBottom: '0.25rem'
        },
        statsLabel: {
            fontSize: '0.85rem',
            color: 'rgba(255,255,255,0.9)',
            textTransform: 'uppercase',
            letterSpacing: '1px'
        },
        controlsContainer: {
            background: 'white',
            padding: '1.5rem',
            borderRadius: '12px',
            marginBottom: '1.5rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        },
        filterGroup: {
            display: 'flex',
            gap: '1rem',
            marginBottom: '1rem',
            flexWrap: 'wrap'
        },
        filterButton: {
            padding: '0.6rem 1.5rem',
            background: 'white',
            border: '2px solid #e1e5e9',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '0.9rem',
            fontWeight: '500',
            transition: 'all 0.2s'
        },
        filterButtonActive: {
            background: '#2a5298',
            color: 'white',
            borderColor: '#2a5298'
        },
        searchContainer: {
            display: 'flex',
            gap: '1rem',
            flexWrap: 'wrap'
        },
        searchInput: {
            flex: 1,
            minWidth: '300px',
            padding: '0.75rem 1rem',
            border: '2px solid #e1e5e9',
            borderRadius: '8px',
            fontSize: '0.95rem',
            transition: 'border-color 0.2s'
        },
        refreshButton: {
            padding: '0.75rem 1.5rem',
            background: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            transition: 'all 0.2s'
        },
        tableContainer: {
            background: 'white',
            borderRadius: '12px',
            overflow: 'auto',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            marginBottom: '1.5rem'
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
            minWidth: '800px'
        },
        th: {
            textAlign: 'left',
            padding: '1rem',
            background: '#f8f9fa',
            borderBottom: '2px solid #dee2e6',
            fontWeight: '600',
            color: '#555',
            fontSize: '0.9rem',
            cursor: 'pointer',
            userSelect: 'none',
            transition: 'background 0.2s'
        },
        thHover: {
            background: '#e9ecef'
        },
        td: {
            padding: '1rem',
            borderBottom: '1px solid #eee',
            fontSize: '0.9rem'
        },
        actionButton: {
            padding: '0.4rem 0.8rem',
            margin: '0 0.25rem',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.8rem',
            fontWeight: '500',
            transition: 'all 0.2s'
        },
        statusBadge: {
            display: 'inline-block',
            padding: '0.3rem 0.8rem',
            borderRadius: '20px',
            fontSize: '0.8rem',
            fontWeight: '600'
        },
        verifiedBadge: {
            display: 'inline-block',
            padding: '0.3rem 0.8rem',
            borderRadius: '20px',
            fontSize: '0.8rem',
            fontWeight: '600'
        },
        pagination: {
            display: 'flex',
            justifyContent: 'center',
            gap: '0.5rem',
            marginTop: '1.5rem',
            flexWrap: 'wrap'
        },
        pageButton: {
            padding: '0.5rem 1rem',
            border: '1px solid #ddd',
            background: 'white',
            borderRadius: '4px',
            cursor: 'pointer',
            transition: 'all 0.2s'
        },
        activePageButton: {
            padding: '0.5rem 1rem',
            border: '1px solid #2a5298',
            background: '#2a5298',
            color: 'white',
            borderRadius: '4px',
            cursor: 'pointer'
        },
        modalOverlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            padding: '1rem'
        },
        modalContent: {
            background: 'white',
            borderRadius: '12px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
        },
        modalHeader: {
            padding: '1.5rem',
            borderBottom: '1px solid #eee',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            position: 'sticky',
            top: 0,
            background: 'white'
        },
        modalTitle: {
            fontSize: '1.5rem',
            fontWeight: '600',
            color: '#333',
            margin: 0
        },
        closeButton: {
            background: 'none',
            border: 'none',
            fontSize: '1.5rem',
            cursor: 'pointer',
            color: '#999',
            padding: '0.5rem'
        },
        modalBody: {
            padding: '1.5rem'
        },
        modalSection: {
            marginBottom: '1.5rem',
            padding: '1rem',
            background: '#f8f9fa',
            borderRadius: '8px'
        },
        modalLabel: {
            fontWeight: '600',
            color: '#555',
            marginBottom: '0.5rem',
            fontSize: '0.9rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
        },
        modalValue: {
            color: '#333',
            lineHeight: '1.6'
        },
        modalFooter: {
            padding: '1.5rem',
            borderTop: '1px solid #eee',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '1rem'
        },
        loadingContainer: {
            textAlign: 'center',
            padding: '3rem',
            color: '#666'
        },
        errorContainer: {
            background: '#f8d7da',
            color: '#721c24',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
        },
        userAvatar: {
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: '#2a5298',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'bold',
            fontSize: '1rem'
        },
        loadingSpinner: {
            display: 'inline-block',
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #2a5298',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
        },
        sortableHeader: {
            cursor: 'pointer',
            userSelect: 'none',
            transition: 'background-color 0.2s'
        }
    };
    
    // Calculate total pages from backend response
    const totalPages = Math.ceil(totalUsers / usersPerPage);
    
    return (
        <div style={styles.container}>
            {/* Success Toast */}
            {showSuccessToast && (
                <div style={styles.toast}>
                    ✓ {successMessage}
                </div>
            )}
            
            {/* Header */}
            <div style={styles.header}>
                <div style={styles.headerLeft}>
                    <img src={UAVSphere_logo} alt="UAVSphere" style={styles.logo} />
                    <div>
                        <h1 style={styles.title}>All Providers List</h1>
                        <p style={styles.subtitle}>View and manage all registered providers</p>
                    </div>
                </div>
                <div style={styles.headerRight}>
                    <div style={styles.statsCard}>
                        <div style={styles.statsNumber}>{totalUsers}</div>
                        <div style={styles.statsLabel}>Total Users</div>
                    </div>
                    <button style={styles.backButton} onClick={handleBackToDashboard}>
                        ← Back to Dashboard
                    </button>
                </div>
            </div>
            
            {/* Controls */}
            <div style={styles.controlsContainer}>
                {/* Status Filter Buttons */}
                <div style={styles.filterGroup}>
                    <button 
                        style={{
                            ...styles.filterButton,
                            ...(statusFilter === '' ? styles.filterButtonActive : {})
                        }}
                        onClick={() => setStatusFilter('')}
                    >
                        All Status
                    </button>
                    <button 
                        style={{
                            ...styles.filterButton,
                            ...(statusFilter === 'active' ? styles.filterButtonActive : {}),
                            background: statusFilter === 'active' ? '#28a745' : 'white',
                            color: statusFilter === 'active' ? 'white' : '#333'
                        }}
                        onClick={() => setStatusFilter('active')}
                    >
                        Active
                    </button>
                    <button 
                        style={{
                            ...styles.filterButton,
                            ...(statusFilter === 'inactive' ? styles.filterButtonActive : {}),
                            background: statusFilter === 'inactive' ? '#dc3545' : 'white',
                            color: statusFilter === 'inactive' ? 'white' : '#333'
                        }}
                        onClick={() => setStatusFilter('inactive')}
                    >
                        Inactive
                    </button>
                </div>
                
                {/* Search Box */}
                <div style={styles.searchContainer}>
                    <input
                        type="text"
                        placeholder="Search by name, email, company, phone number, city, country, or Partner No..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={styles.searchInput}
                    />
                    <button 
                        style={styles.refreshButton}
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                    >
                        {isRefreshing ? '⟳' : '🔄'} Refresh
                    </button>
                </div>
            </div>
            
            {/* Error Message */}
            {error && (
                <div style={styles.errorContainer}>
                    ⚠️ {error}
                    <button 
                        onClick={() => dispatch(clearError())}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem' }}
                    >
                        ✕
                    </button>
                </div>
            )}
            
            {/* Users Table */}
            <div style={styles.tableContainer}>
                {loading ? (
                    <div style={styles.loadingContainer}>
                        <div style={styles.loadingSpinner}></div>
                        <div style={{ marginTop: '1rem' }}>Loading users...</div>
                    </div>
                ) : (
                    <>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>User</th>
                                    <th style={styles.th}>Contact Info</th>
                                    <th style={styles.th}>Company</th>
                                    <th style={styles.th}>Type</th>
                                    <th style={styles.th}>Location</th>
                                    <th 
                                        style={styles.th}
                                        onClick={() => handleSort('partner_no')}
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#e9ecef'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = '#f8f9fa'}
                                    >
                                        Partner No{getSortIcon('partner_no')}
                                    </th>
                                    <th 
                                        style={styles.th}
                                        onClick={() => handleSort('signup_time')}
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#e9ecef'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = '#f8f9fa'}
                                    >
                                        Joined{getSortIcon('signup_time')}
                                    </th>
                                    <th style={styles.th}>Status</th>
                                    <th style={styles.th}>Verified</th>
                                    <th style={styles.th}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map(user => (
                                    <tr key={user._id}>
                                        <td style={styles.td}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                                <div style={styles.userAvatar}>
                                                    {user.firstname?.charAt(0)}{user.lastname?.charAt(0)}
                                                </div>
                                                <div>
                                                    <div><strong>{user.firstname || 'N/A'} {user.lastname || 'N/A'}</strong></div>
                                                    <div style={{ fontSize: '0.75rem', color: '#666' }}>ID: {user._id?.slice(-8)}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td style={styles.td}>
                                            <div>{user.email || 'N/A'}</div>
                                            <div style={{ fontSize: '0.8rem', color: '#666' }}>{user.phonenumber || 'N/A'}</div>
                                        </td>
                                        <td style={styles.td}>
                                            {user.company !== 'NA' ? user.company : '-'}
                                        </td>
                                        <td style={styles.td}>
                                            <span style={{
                                                padding: '0.2rem 0.5rem',
                                                borderRadius: '4px',
                                                background: user.provider_type === 'company' ? '#e3f2fd' : '#f3e5f5',
                                                color: user.provider_type === 'company' ? '#1976d2' : '#9c27b0',
                                                fontSize: '0.75rem',
                                                fontWeight: '600'
                                            }}>
                                                {user.provider_type === 'company' ? '🏢 Company' : '👤 Individual'}
                                            </span>
                                        </td>
                                        <td style={styles.td}>
                                            {user.city || 'N/A'}, {user.country || 'N/A'}
                                        </td>
                                        <td style={styles.td}>
                                            <strong>{user.GeoWGS84_partner_no || 'N/A'}</strong>
                                        </td>
                                        <td style={styles.td}>{formatDate(user.signup_time)}</td>
                                        <td style={styles.td}>{getStatusBadge(user.user_active_status)}</td>
                                        <td style={styles.td}>{getVerifiedBadge(user.verified)}</td>
                                        <td style={styles.td}>
                                            <button
                                                style={{...styles.actionButton, background: '#007bff', color: 'white'}}
                                                onClick={() => handleViewDetails(user)}
                                                title="View Details"
                                            >
                                                👁️ View
                                            </button>
                                             <button
    style={{...styles.actionButton, background: '#dc3545', color: 'white'}}
    onClick={() => handleDeleteClick(user)}
    title="Delete Provider"
>
    🗑️ Delete
</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        
                        {users.length === 0 && (
                            <div style={styles.loadingContainer}>
                                No users found matching your search criteria
                            </div>
                        )}
                    </>
                )}
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
                <div style={styles.pagination}>
                    <button
                        style={styles.pageButton}
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                    >
                        ← Previous
                    </button>
                    
                    {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                        let page;
                        if (totalPages <= 7) {
                            page = i + 1;
                        } else if (currentPage <= 4) {
                            page = i + 1;
                            if (i === 6) page = totalPages;
                        } else if (currentPage >= totalPages - 3) {
                            page = totalPages - 6 + i;
                        } else {
                            page = currentPage - 3 + i;
                            if (i === 0) page = 1;
                            if (i === 6) page = totalPages;
                        }
                        
                        if (page === currentPage) {
                            return (
                                <button key={page} style={styles.activePageButton}>
                                    {page}
                                </button>
                            );
                        }
                        
                        return (
                            <button
                                key={page}
                                style={styles.pageButton}
                                onClick={() => setCurrentPage(page)}
                            >
                                {page}
                            </button>
                        );
                    })}
                    
                    <button
                        style={styles.pageButton}
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                    >
                        Next →
                    </button>
                </div>
            )}
            
            {/* User Details Modal */}
            {showModal && selectedUser && (
                <div style={styles.modalOverlay} onClick={() => setShowModal(false)}>
                    <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                            <h2 style={styles.modalTitle}>User Details: {selectedUser.firstname} {selectedUser.lastname}</h2>
                            <button style={styles.closeButton} onClick={() => setShowModal(false)}>✕</button>
                        </div>
                        
                        <div style={styles.modalBody}>
                            <div style={styles.modalSection}>
                                <div style={styles.modalLabel}>👤 Personal Information</div>
                                <div style={styles.modalValue}>
                                    <p><strong>Full Name:</strong> {selectedUser.firstname || 'N/A'} {selectedUser.lastname || 'N/A'}</p>
                                    <p><strong>Email:</strong> {selectedUser.email || 'N/A'}</p>
                                    <p><strong>Phone:</strong> {selectedUser.phonenumber || 'N/A'}</p>
                                    <p><strong>Provider Type:</strong> {selectedUser.provider_type || 'N/A'}</p>
                                </div>
                            </div>
                            
                            <div style={styles.modalSection}>
                                <div style={styles.modalLabel}>🏢 Company Information</div>
                                <div style={styles.modalValue}>
                                    <p><strong>Company:</strong> {selectedUser.company !== 'NA' ? selectedUser.company : 'Not specified'}</p>
                                    <p><strong>Website:</strong> {selectedUser.companywebsite !== 'NA' ? selectedUser.companywebsite : 'Not specified'}</p>
                                    <p><strong>Partner No:</strong> {selectedUser.GeoWGS84_partner_no || 'N/A'}</p>
                                </div>
                            </div>
                            
                            <div style={styles.modalSection}>
                                <div style={styles.modalLabel}>📍 Location</div>
                                <div style={styles.modalValue}>
                                    <p><strong>Address:</strong> {selectedUser.address || 'N/A'}</p>
                                    <p><strong>City:</strong> {selectedUser.city || 'N/A'}</p>
                                    <p><strong>State:</strong> {selectedUser.state || 'N/A'}</p>
                                    <p><strong>Country:</strong> {selectedUser.country || 'N/A'}</p>
                                </div>
                            </div>
                            
                            <div style={styles.modalSection}>
                                <div style={styles.modalLabel}>📊 Account Status</div>
                                <div style={styles.modalValue}>
                                    <p><strong>Status:</strong> {getStatusBadge(selectedUser.user_active_status)}</p>
                                    <p><strong>Verified:</strong> {getVerifiedBadge(selectedUser.verified)}</p>
                                    <p><strong>Signup Date:</strong> {formatDate(selectedUser.signup_time)}</p>
                                    <p><strong>Terms Accepted:</strong> {selectedUser.terms_and_conditions ? '✓ Yes' : '✗ No'}</p>
                                </div>
                            </div>
                            
                            {selectedUser.services && selectedUser.services.length > 0 && (
                                <div style={styles.modalSection}>
                                    <div style={styles.modalLabel}>🛠️ Services</div>
                                    <div style={styles.modalValue}>
                                        {selectedUser.services.map((service, idx) => (
                                            <span key={idx} style={{
                                                display: 'inline-block',
                                                padding: '0.2rem 0.5rem',
                                                background: '#e3f2fd',
                                                margin: '0.25rem',
                                                borderRadius: '4px',
                                                fontSize: '0.85rem'
                                            }}>{service}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <div style={styles.modalFooter}>
                            <button
                                style={{...styles.actionButton, background: '#6c757d', color: 'white', padding: '0.6rem 1.5rem'}}
                                onClick={() => setShowModal(false)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Delete Confirmation Modal */}
{userToDelete && (
    <div style={styles.modalOverlay} onClick={cancelDelete}>
        <div
            style={{...styles.modalContent, maxWidth: '520px'}}
            onClick={(e) => e.stopPropagation()}
        >
            <div style={styles.modalHeader}>
                <h2 style={{...styles.modalTitle, color: '#dc3545'}}>
                    ⚠️ Delete Provider
                </h2>
                <button
                    style={styles.closeButton}
                    onClick={cancelDelete}
                    disabled={isDeleting}
                >
                    ✕
                </button>
            </div>

            <div style={styles.modalBody}>
                <p style={{ marginBottom: '1rem', fontSize: '1rem' }}>
                    Are you sure you want to permanently delete{' '}
                    <strong>
                        {[userToDelete.firstname, userToDelete.lastname]
                            .filter(Boolean)
                            .join(' ') || userToDelete.email}
                    </strong>
                    {userToDelete.email && (
                        <> (<span style={{ color: '#666' }}>{userToDelete.email}</span>)</>
                    )}
                    ?
                </p>

                <div style={{
                    background: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '8px',
                    padding: '1rem',
                    marginBottom: '1rem'
                }}>
                    <div style={{
                        fontWeight: '600',
                        color: '#856404',
                        marginBottom: '0.5rem'
                    }}>
                        This will remove:
                    </div>
                    <ul style={{
                        margin: 0,
                        paddingLeft: '1.25rem',
                        color: '#856404',
                        lineHeight: '1.7'
                    }}>
                        <li>The provider account</li>
                        <li>Drone payload data</li>
                        <li>Their applications</li>
                        <li>Their geometry KML file</li>
                        <li>Their references in customer leads</li>
                    </ul>
                </div>

                <div style={{
                    background: '#f8d7da',
                    border: '1px solid #dc3545',
                    borderRadius: '8px',
                    padding: '0.75rem 1rem',
                    color: '#721c24',
                    fontWeight: '600',
                    fontSize: '0.9rem',
                    textAlign: 'center'
                }}>
                    ⚠️ This action CANNOT be undone.
                </div>
            </div>

            <div style={styles.modalFooter}>
                <button
                    style={{
                        ...styles.actionButton,
                        background: '#6c757d',
                        color: 'white',
                        padding: '0.6rem 1.5rem'
                    }}
                    onClick={cancelDelete}
                    disabled={isDeleting}
                >
                    Cancel
                </button>
                <button
                    style={{
                        ...styles.actionButton,
                        background: '#dc3545',
                        color: 'white',
                        padding: '0.6rem 1.5rem',
                        opacity: isDeleting ? 0.6 : 1,
                        cursor: isDeleting ? 'not-allowed' : 'pointer'
                    }}
                    onClick={confirmDelete}
                    disabled={isDeleting}
                >
                    {isDeleting ? '⟳ Deleting...' : '🗑️ Yes, Delete'}
                </button>
            </div>
        </div>
    </div>
)}
            
            <style>
                {`
                    @keyframes slideIn {
                        from {
                            transform: translateX(100%);
                            opacity: 0;
                        }
                        to {
                            transform: translateX(0);
                            opacity: 1;
                        }
                    }
                    
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                    
                    th:hover {
                        background-color: #e9ecef !important;
                    }
                `}
            </style>
        </div>
    );
};

export default UsersList;